/**
 * Camunda 7.18 Batch REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/batch/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Batch,
  BatchQuery,
  BatchStatistics,
  CountResult,
} from '../types';

export class BatchService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/batch';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get batches
   */
  async getList(query?: BatchQuery): Promise<Batch[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Batch[]>(`${this.basePath}${params}`);
  }

  /**
   * Get batches count
   */
  async getCount(query?: BatchQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get batch by ID
   */
  async getById(id: string): Promise<Batch> {
    return this.client.get<Batch>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Statistics
  // ============================================================================

  /**
   * Get batch statistics
   */
  async getStatistics(query?: BatchQuery): Promise<BatchStatistics[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<BatchStatistics[]>(`${this.basePath}/statistics${params}`);
  }

  /**
   * Get batch statistics count
   */
  async getStatisticsCount(query?: BatchQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/statistics/count${params}`);
  }

  // ============================================================================
  // Suspension
  // ============================================================================

  /**
   * Activate/Suspend batch
   */
  async updateSuspensionState(id: string, suspended: boolean): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/suspended`, { suspended });
  }

  // ============================================================================
  // Delete
  // ============================================================================

  /**
   * Delete batch
   */
  async delete(id: string, cascade?: boolean): Promise<void> {
    const params = cascade ? '?cascade=true' : '';
    await this.client.delete<void>(`${this.basePath}/${id}${params}`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: BatchQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let batchServiceInstance: BatchService | null = null;

export function getBatchService(): BatchService {
  if (!batchServiceInstance) {
    batchServiceInstance = new BatchService();
  }
  return batchServiceInstance;
}

export default BatchService;
